import React, { FC, useEffect, useState } from "react";
import { IconButton, Stack, TableSortLabel, Typography } from "@mui/material";
import { ProcurementSupplierDto } from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { ConfirmationModal, Table } from "@components/common";
import { TableColumn } from "@components/common/Table";
import {
  useDeleteProcurementSupplier,
  useGetCurrentCompanyInfo,
  useGetProcurementSuppliers,
  useUpdateProcurementSupplier,
} from "@api";
import { Chip } from "@components/base";
import { PaginationProps } from "@components/common/Table/Table.types";
import { useUserStore } from "@store/user";
import { formatDateUtc } from "@helpers";
import { useBuyersHubStore } from "@components/main/procurement";

interface SuppliersTable {
  onRowClick: (record: ProcurementSupplierDto) => void;
  search?: string;
  orderBy?: string;
  orderDirection: "ASC" | "DESC";
  setOrderBy: (key: string) => void;
  setOrderDirection: (dir: "ASC" | "DESC") => void;
}

const SuppliersTable: FC<SuppliersTable> = ({
  onRowClick,
  search,
  orderBy,
  orderDirection,
  setOrderBy,
  setOrderDirection,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { setSupplierIds, supplierIds } = useBuyersHubStore();

  const [confirmDeleteSupplier, setConfirmDeleteSupplier] =
    useState<ProcurementSupplierDto>();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const { deleteProcurementSupplier, deleteProcurementSupplierEnabled } =
    useDeleteProcurementSupplier(user);
  const { updateProcurementSupplierEnabled } = useUpdateProcurementSupplier(user);
  const {
    procurementSuppliers,
    procurementSuppliersEnabled,
    isPendingGetProcurementSuppliers,
  } = useGetProcurementSuppliers(
    user,
    search,
    orderBy,
    orderDirection,
    page,
    pageSize,
  );

  useEffect(() => {
    setPage(1);
  }, [search, orderBy, orderDirection]);

  const handleDeleteSupplier = (): void => {
    if (confirmDeleteSupplier) {
      deleteProcurementSupplier(confirmDeleteSupplier.id);
      setSupplierIds([]);
    }
    setConfirmDeleteSupplier(undefined);
  };

  const getStatusBadges = (record: ProcurementSupplierDto): JSX.Element => {
    const now = dayjs();
    const deadline = dayjs(record.deadline);

    const isDeadlinePassed = deadline.isBefore(now, "day");

    const showSubmitted = !!record.isDataSubmited;

    return (
      <Stack direction="row" justifyContent="flex-start" spacing={1}>
        <Chip
          label={t(
            record.isRegistrationLinkSent ? "sellers.invited" : "sellers.notInvited",
          )}
          color={record.isRegistrationLinkSent ? "blue2" : "orange1"}
          variant="filled"
          size="medium"
        />
        <Chip
          label={t(
            record.isSellerRegistered
              ? "sellers.registered"
              : "sellers.notRegistered",
          )}
          color={record.isSellerRegistered ? "green" : "error"}
          variant="filled"
          size="medium"
        />
        {/* TODO: add when BE is ready <Chip
          label={t(record.isSharingData ? "Sharing data ON" : "Sharing data OFF")}
          color={record.isSellerRegistered ? "blue2" : "orange1"}
          variant="filled"
          size="medium"
        />*/}
        {showSubmitted ? (
          <Chip
            label={t("sellers.dataSubmitted")}
            color="orange1"
            variant="filled"
            size="medium"
          />
        ) : isDeadlinePassed ? (
          <Chip
            label={t("sellers.deadlineExpired")}
            color="purple3"
            variant="filled"
            size="medium"
          />
        ) : (
          <Chip
            label={t("sellers.dataNotSubmitted")}
            color="error"
            variant="filled"
            size="medium"
          />
        )}
      </Stack>
    );
  };

  const columns: TableColumn<ProcurementSupplierDto>[] = [
    {
      title: (
        <TableSortLabel
          active={orderBy === "name"}
          direction={orderDirection.toLowerCase() as "asc" | "desc"}
          onClick={() => {
            setOrderBy("name");
            setOrderDirection(orderDirection === "ASC" ? "DESC" : "ASC");
          }}
        >
          {t("sellers.entityName")}
        </TableSortLabel>
      ),
      dataKey: "name",
      key: "name",
    },
    {
      title: (
        <TableSortLabel
          active={orderBy === "email"}
          direction={orderDirection.toLowerCase() as "asc" | "desc"}
          onClick={() => {
            setOrderBy("email");
            setOrderDirection(orderDirection === "ASC" ? "DESC" : "ASC");
          }}
        >
          {t("sellers.email")}
        </TableSortLabel>
      ),
      dataKey: "email",
      key: "email",
    },
    {
      title: (
        <TableSortLabel
          active={orderBy === "vat"}
          direction={orderDirection.toLowerCase() as "asc" | "desc"}
          onClick={() => {
            setOrderBy("vat");
            setOrderDirection(orderDirection === "ASC" ? "DESC" : "ASC");
          }}
        >
          {t("sellers.vat")}
        </TableSortLabel>
      ),
      dataKey: "vat",
      key: "vat",
    },
    {
      title: (
        <TableSortLabel
          active={orderBy === "deadline"}
          direction={orderDirection.toLowerCase() as "asc" | "desc"}
          onClick={() => {
            setOrderBy("annualProcurementValue");
            setOrderDirection(orderDirection === "ASC" ? "DESC" : "ASC");
          }}
        >
          {t("sellers.deadline")}
        </TableSortLabel>
      ),
      dataKey: "deadline",
      key: "deadline",
      renderCellContent: (record) => {
        return record?.deadline ? formatDateUtc(record.deadline) : " - ";
      },
    },
    {
      title: t("sellers.status"),
      dataKey: "isRegistrationLinkSent",
      key: "registrationLinkSent",
      renderCellContent: (record) => getStatusBadges(record),
    },
    {
      title: t("common.actions"),
      key: "actions",
      tableCellProps: {
        width: 100,
        align: "center",
      },
      renderCellContent: (record) => (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {updateProcurementSupplierEnabled && (
            <IconButton
              color="primary"
              size="small"
              onClick={() => onRowClick(record)}
            >
              <FeatherIcon icon="edit" size={16} />
            </IconButton>
          )}
          {deleteProcurementSupplierEnabled && (
            <IconButton
              color="error"
              size="small"
              onClick={() => setConfirmDeleteSupplier(record)}
            >
              <FeatherIcon icon="trash-2" size={16} />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  const pagination: PaginationProps = {
    onPageNumberChange: (pageNumber) => {
      setPage(pageNumber);
    },
    onPageSizeChange: (newSize) => {
      const newPage = Math.floor(((page - 1) * pageSize) / newSize) + 1;

      setPageSize(newSize);
      setPage(newPage);
    },
    pageNumber: page,
    pageCount: procurementSuppliers?.totalPages,
    totalItems: procurementSuppliers?.totalCount,
    pageSize: pageSize,
    pageSizeOptions: [10, 20, 50],
  };

  return (
    procurementSuppliersEnabled && (
      <>
        <Table<ProcurementSupplierDto>
          columns={columns}
          data={procurementSuppliers?.items || []}
          dataPlaceholder={
            isPendingGetProcurementSuppliers ? undefined : (
              <Typography textAlign="center">{t("sellers.noAdded")}</Typography>
            )
          }
          enableRowSelection
          selectedRowIds={supplierIds}
          onSelectionChange={(ids) => setSupplierIds(ids)}
          pagination={pagination}
        />
        <ConfirmationModal
          open={confirmDeleteSupplier !== undefined}
          onClose={() => setConfirmDeleteSupplier(undefined)}
          onConfirm={handleDeleteSupplier}
          title={t("common.confirmAction")}
          description={t("sellers.confirmDelete", {
            supplier: confirmDeleteSupplier?.name,
            company: currentCompanyInfo?.name,
          })}
        />
      </>
    )
  );
};

export default SuppliersTable;
